<script setup lang="ts">
import { DialogService } from '@/core/shared/dialog/dialog.service';
import { computed, nextTick, onMounted, ref } from 'vue';
import BaseModal from '@/components/Shared/Modals/BaseModal.vue';
import MessageSentAnimation from '@/components/Sourcing/MessageSentAnimation.vue';
import ConversationMessageService from '@/core/conversations/message/conversation-message.service';
import { SnackbarService } from '@/core/shared/snackbar/snackbar.service';
import { ErrorService } from '@/core/shared/errors/error.service';
import { TEMPLATE_CHIPS, isCustomTemplate } from '@/core/conversations/template/template.utils';
import TemplateService from '@/core/conversations/template/template.service';
import TemplateChip from '@/components/Conversations/Templates/TemplateChip.vue';
import TemplateCard from '@/components/Conversations/Templates/TemplateCard.vue';
import PlusCircleSvg from '@/assets/svg/plus-circle-20.svg?component';

const emit = defineEmits<{
  (e: 'close', value: { messageSent: boolean }): void;
}>();

const dialogService = new DialogService();
const conversationMessageService = new ConversationMessageService();
const templateService = new TemplateService();

const sendingMessage = ref(false);
const messageSentFlag = ref(false);
const message = ref('');
const textAreaEl = ref<HTMLTextAreaElement | null>(null);

const showTemplateSelector = ref(false);

const modalProps = computed(() => dialogService.bulkMessagingModalProps);
const isOpen = computed(() => modalProps.value.isOpen);

const disableSendButton = computed(() => {
  return !message.value || !message.value.trim() || sendingMessage.value;
});

async function close({ messageSent }: { messageSent: boolean }) {
  emit('close', { messageSent });
  await nextTick();
  resetForm();

  // Wait for close animation to finish
  setTimeout(() => {
    // If messages were sent, try to scroll the message list to bottom
    if (messageSent) {
      conversationMessageService.scrollMessagesToBottom = true;
    }
    messageSentFlag.value = false;
  }, 500);
}

function resetForm() {
  message.value = '';
}

async function sendMessage() {
  if (disableSendButton.value) return;

  sendingMessage.value = true;

  try {
    await conversationMessageService.sendBulkMessagesToConversation(
      modalProps.value.conversationIndexes,
      message.value,
    );
    messageSentFlag.value = true;
  } catch (error) {
    ErrorService.captureException(error);
    SnackbarService.criticalAlt(
      error instanceof Error
        ? `${error.message}. Please try again later`
        : 'Failed to send messages to the selected candidates. Please try again.',
    );
  } finally {
    sendingMessage.value = false;
  }
}

const insertChip = (chipText: string) => {
  if (!chipText.length) return;
  templateService.insertChip(chipText, textAreaEl.value, message.value, (newText) => {
    message.value = newText;
  });
};

const showHeading = computed(() => !messageSentFlag.value);
const showActions = computed(() => !messageSentFlag.value && !showTemplateSelector.value);
const title = computed(() => {
  if (showTemplateSelector.value) {
    return 'Use a template';
  }
  return 'Send bulk messages';
});
const subtitle = computed(() => {
  if (showTemplateSelector.value) {
    return 'Quickly message candidates using pre-made templates';
  }
  return '';
});
const backButtonText = computed(() => {
  if (showTemplateSelector.value) {
    return 'Back to editing';
  }
  return '';
});

const templates = computed(() => templateService.templates);

const selectTemplate = (templateId: number) => {
  const templateSelected = templateService.templates.find((template) => template.id === templateId);
  if (templateSelected) {
    message.value = templateSelected.message;
    showTemplateSelector.value = false;

    // Lets focus the textarea after the template is inserted
    // must wait for the element to be rendered
    setTimeout(() => {
      textAreaEl.value?.focus();
    }, 500);
  }
};
// BaseModal's X emits `cancel` unconditionally. Keep this modal's long-standing behavior
// of treating it as "back" while the template selector is open.
function onCancel() {
  if (showTemplateSelector.value) {
    showTemplateSelector.value = false;
    return;
  }
  close({ messageSent: false });
}

onMounted(() => {
  templateService.fetchTemplates();
});
</script>

<template>
  <BaseModal
    :model-value="isOpen"
    :is-loading="sendingMessage"
    :is-submit-disabled="disableSendButton"
    :show-close-button="true"
    :show-cancel-button="true"
    :show-heading="showHeading"
    :show-actions="showActions"
    max-width="960px"
    :title="title"
    :subtitle="subtitle"
    :back-button-text="backButtonText"
    submit-button-text="Send messages"
    @submit="sendMessage"
    @cancel="onCancel"
    @back="showTemplateSelector = false"
  >
    <MessageSentAnimation
      v-if="messageSentFlag"
      @animation:complete="close({ messageSent: true })"
    ></MessageSentAnimation>
    <div v-else>
      <div v-show="!showTemplateSelector">
        <!-- Textarea -->
        <div class="mt-5">
          <span class="text-sm font-bold">Message</span>
          <textarea
            ref="textAreaEl"
            id="bulk-message-textarea"
            v-model="message"
            :disabled="sendingMessage"
            class="mt-1 w-full resize-none rounded-md border border-tint-80 px-4 py-4 text-shade-900 placeholder:text-shade-800 focus:outline-none focus:ring-2 focus:ring-highlight-500"
            placeholder="Type your message here"
            rows="8"
          ></textarea>
        </div>
        <!-- Dynamic Variables Chips -->
        <div class="mb-6 mt-4 flex flex-wrap content-center items-center justify-between">
          <div>
            <v-btn
              @click="showTemplateSelector = true"
              class="modal-button-secondary"
              :ripple="false"
              variant="flat"
            >
              <PlusCircleSvg /> Use a template
            </v-btn>
          </div>
          <div class="flex flex-wrap justify-end gap-2">
            <TemplateChip
              v-for="(chip, index) in TEMPLATE_CHIPS"
              :key="index"
              :text="chip"
              class="cursor-default transition-opacity duration-300"
              @insert-chip="insertChip"
            />
          </div>
        </div>
      </div>

      <div v-show="showTemplateSelector">
        <div class="templates-scrollbar pb-0.5">
          <template-card
            v-for="template in templates"
            :key="template.id"
            :id="template.id"
            :title="template.title"
            :custom="isCustomTemplate(template)"
            :templateText="template.message"
            preview-mode="raw"
            :show-send="false"
            :can-manage="false"
            @insert-template="selectTemplate"
          />
        </div>
      </div>
    </div>
  </BaseModal>
</template>
<style lang="postcss" scoped>
.templates-scrollbar {
  @apply mr-2 mt-8 grid h-[340px] grid-cols-2 gap-6 overflow-auto pr-4;

  /** Scrollbar width */
  &::-webkit-scrollbar {
    width: 8px;
  }

  /** Scrollbar color and border radius */
  &::-webkit-scrollbar-thumb {
    @apply bg-tint-80;
    border-radius: 8px;
  }
}
</style>
